/**
 * <cart-shipping-estimate>
 *
 * Toont een geschatte verzendkost + totaal inclusief verzending op de
 * cart-pagina, direct onder het "Geschat totaal".
 *
 * BELANGRIJK: er wordt GEEN live request naar /cart/shipping_rates.json gedaan
 * en GEEN postcode meegestuurd — een postcode lekt door naar het checkout-adres.
 * In plaats daarvan wordt alles client-side berekend uit de geëxporteerde
 * tarieventabel (assets/shipping_cost_by_product.json).
 *
 * Logica:
 *   1. Groepeer de cart-regels per verzendprofiel (per product opgezocht in de
 *      tabel; onbekende producten vallen terug op het default profiel).
 *   2. Bepaal per profiel het groep-subtotaal (som van final_line_price).
 *   3. Kies per profiel het tarief waarvan de TOTAL_PRICE-conditie matcht
 *      (gratis-drempels, XL-tiers).
 *   4. Tel de tarieven van alle profielen op (= split shipments).
 *
 * Data-attributen (gezet vanuit snippets/cart-shipping-estimate.liquid):
 *   data-country-code  ISO-landcode van de bestemming (bv. "DE")
 *   data-rates-url     asset-URL van shipping_cost_by_product.json
 *   data-subtotal      cart.total_price in centen
 *
 * Graceful degradation: bij een niet-ondersteund land, lege cart, ontbrekende
 * data of een fout blijft het element verborgen en blijft het normale totaal
 * ongemoeid.
 */
class CartShippingEstimate extends HTMLElement {
  constructor() {
    super();

    this.countryCode = (this.dataset.countryCode || '').toUpperCase();
    this.ratesUrl = this.dataset.ratesUrl;
    // Use the "with currency" format (e.g. "€{{amount}} EUR") so the rows match
    // the server-rendered "Geschat totaal" line. Falls back to the global format.
    this.moneyFormat = this.dataset.moneyFormat || FoxTheme.settings.moneyFormat;
    this.subtotalCents = parseInt(this.dataset.subtotal, 10) || 0;

    this.shippingValueElement = this.querySelector('.totals__shipping-value');
    this.totalValueElement = this.querySelector('.totals__estimate-value');
    this.noteElement =
      this.nextElementSibling && this.nextElementSibling.classList.contains('shipping-estimate-note')
        ? this.nextElementSibling
        : null;

    this.items = this.readEmbeddedItems();
    this.ratesData = null;
    this.isLoading = false;

    // Info "(i)" tooltip — toggled on click/tap so it works on touch devices
    // too (the theme's hover tooltip only fires on pointer: fine).
    this.infoToggle = this.querySelector('.shipping-estimate-info');
    this.onInfoToggleClick = this.onInfoToggleClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.estimate();

    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    if (this.infoToggle) {
      this.infoToggle.addEventListener('click', this.onInfoToggleClick);
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.infoToggle) {
      this.infoToggle.removeEventListener('click', this.onInfoToggleClick);
    }
    this.closeInfo();
  }

  onInfoToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.infoToggle.classList.contains('tooltip--is-visible')) {
      this.closeInfo();
    } else {
      this.openInfo();
    }
  }

  openInfo() {
    if (!this.infoToggle) return;
    this.infoToggle.classList.add('tooltip--is-visible');
    this.infoToggle.setAttribute('aria-expanded', 'true');
    this.positionTooltip();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  closeInfo() {
    if (!this.infoToggle) return;
    this.infoToggle.classList.remove('tooltip--is-visible');
    this.infoToggle.setAttribute('aria-expanded', 'false');
    const tooltip = this.infoToggle.querySelector('.shipping-estimate-info__tooltip');
    if (tooltip) tooltip.removeAttribute('style');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  // On mobile the icon sits mid-row, so an icon-anchored tooltip overflows the
  // viewport. Pin it fixed + horizontally centered on the screen (just below the
  // icon) so it's always fully visible. Desktop keeps the pure-CSS positioning.
  positionTooltip() {
    const tooltip = this.infoToggle.querySelector('.shipping-estimate-info__tooltip');
    if (!tooltip) return;

    if (!window.matchMedia('(max-width: 749px)').matches) {
      tooltip.removeAttribute('style');
      return;
    }

    const rect = this.infoToggle.getBoundingClientRect();
    tooltip.style.position = 'fixed';
    tooltip.style.insetInlineStart = '50%';
    tooltip.style.insetInlineEnd = 'auto';
    tooltip.style.insetBlockStart = `${Math.round(rect.bottom + 8)}px`;
    tooltip.style.insetBlockEnd = 'auto';
    tooltip.style.zIndex = '50';
    tooltip.style.setProperty('--translate-x', '-50%');
  }

  onDocumentClick(event) {
    if (this.infoToggle && !this.infoToggle.contains(event.target)) {
      this.closeInfo();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.closeInfo();
      if (this.infoToggle) this.infoToggle.focus();
    }
  }

  readEmbeddedItems() {
    const el = this.querySelector('.cart-shipping-estimate__items');
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  onCartUpdate(event) {
    const cart = event && event.cart;
    if (!cart) return;

    if (Array.isArray(cart.items)) {
      this.items = cart.items.map((i) => ({ id: i.product_id, price: i.final_line_price }));
      if (typeof cart.total_price !== 'undefined') this.subtotalCents = cart.total_price;
      this.estimate();
    } else {
      // Some publishers don't include line items — fetch the fresh cart.
      this.refreshFromCart();
    }
  }

  async refreshFromCart() {
    try {
      const url = `${FoxTheme.routes.cart_url}.js`.replace('//', '/');
      const response = await fetch(url);
      const cart = await response.json();
      this.items = (cart.items || []).map((i) => ({ id: i.product_id, price: i.final_line_price }));
      this.subtotalCents = cart.total_price;
      this.estimate();
    } catch (e) {
      this.hide();
    }
  }

  async estimate() {
    if (this.isLoading) return;
    if (!this.countryCode || !this.items.length) {
      this.hide();
      return;
    }

    this.isLoading = true;
    try {
      const data = await this.loadRates();
      if (!data) {
        this.hide();
        return;
      }

      // Group line subtotals per delivery profile.
      const groups = {};
      for (const item of this.items) {
        const profileId = data.productProfile[String(item.id)] || data.defaultProfile;
        if (!profileId) {
          this.hide();
          return;
        }
        groups[profileId] = (groups[profileId] || 0) + (item.price || 0);
      }

      // Pick the matching rate per profile and sum across profiles. Also flag
      // whether the cart contains a specialized XL-transport product, so the
      // (i) explanation is only shown when it actually applies.
      let totalShipping = 0;
      let hasXL = false;
      for (const profileId in groups) {
        const byCountry = data.profiles[profileId];
        const rates = byCountry && byCountry[this.countryCode];
        if (!rates || !rates.length) {
          // Country not supported for this profile — bail out gracefully.
          this.hide();
          return;
        }
        const rate = this.pickRate(rates, groups[profileId]);
        if (!rate) {
          this.hide();
          return;
        }
        totalShipping += rate.price;
        if (data.xlProfiles && data.xlProfiles.has(profileId)) hasXL = true;
      }

      this.render(totalShipping, hasXL);
    } catch (e) {
      this.hide();
    } finally {
      this.isLoading = false;
    }
  }

  async loadRates() {
    if (this.ratesData) return this.ratesData;
    if (!this.ratesUrl) return null;
    try {
      const response = await fetch(this.ratesUrl);
      const raw = await response.json();
      this.ratesData = this.indexRates(raw);
      return this.ratesData;
    } catch (e) {
      return null;
    }
  }

  /**
   * Transform the exported product array into fast lookup tables:
   *   productProfile: { [productId]: profileId }
   *   profiles:       { [profileId]: { [ISO]: [{ price, min, max }] } }  (cents)
   *   defaultProfile: profileId used for products absent from the table
   *   xlProfiles:     Set of profileIds whose rate is specialized XL transport
   *                   (any method name contains "XL", across languages)
   */
  indexRates(raw) {
    const productProfile = {};
    const profiles = {};
    const xlProfiles = new Set();
    let defaultProfile = null;

    if (!Array.isArray(raw)) return { productProfile, profiles, defaultProfile, xlProfiles };

    raw.forEach((p) => {
      const profileId = this.gidToId(p.profile_id);
      const productId = this.gidToId(p.product_id);
      if (productId) productProfile[productId] = profileId;
      if (p.default_profile && !defaultProfile) defaultProfile = profileId;

      if (!profiles[profileId]) {
        profiles[profileId] = {};
        Object.keys(p.rates_by_country || {}).forEach((cc) => {
          const country = p.rates_by_country[cc];
          const rates = (country.rates || [])
            .filter((r) => r.active)
            .map((r) => {
              if (r.method_name && r.method_name.toUpperCase().indexOf('XL') !== -1) {
                xlProfiles.add(profileId);
              }
              return {
                price: this.toCents(r.price_amount),
                min: r.min_value != null ? this.toCents(r.min_value) : 0,
                max: r.max_value != null ? this.toCents(r.max_value) : null,
              };
            });
          profiles[profileId][cc.toUpperCase()] = rates;
        });
      }
    });

    return { productProfile, profiles, defaultProfile, xlProfiles };
  }

  pickRate(rates, subtotalCents) {
    const matches = rates.filter(
      (r) => subtotalCents >= r.min && (r.max === null || subtotalCents <= r.max)
    );
    if (!matches.length) return null;
    // Defensive: if ranges overlap, prefer the cheapest applicable rate.
    return matches.reduce((a, b) => (b.price < a.price ? b : a));
  }

  toCents(value) {
    return Math.round(parseFloat(value) * 100);
  }

  gidToId(gid) {
    if (gid == null) return null;
    const match = String(gid).match(/(\d+)\s*$/);
    return match ? match[1] : String(gid);
  }

  render(shippingCents, hasXL) {
    if (isNaN(shippingCents)) {
      this.hide();
      return;
    }

    // Only surface the (i) XL-transport explanation when the cart actually
    // contains a specialized XL-transport product.
    if (this.infoToggle) {
      if (hasXL) {
        this.infoToggle.hidden = false;
      } else {
        this.closeInfo();
        this.infoToggle.hidden = true;
      }
    }

    const format = this.moneyFormat;

    if (this.shippingValueElement) {
      this.shippingValueElement.textContent = FoxTheme.Currency.formatMoney(shippingCents, format);
    }
    if (this.totalValueElement) {
      this.totalValueElement.textContent = FoxTheme.Currency.formatMoney(
        this.subtotalCents + shippingCents,
        format
      );
    }

    this.hidden = false;
    if (this.noteElement) {
      this.noteElement.hidden = false;
    }
  }

  hide() {
    this.hidden = true;
    if (this.noteElement) {
      this.noteElement.hidden = true;
    }
  }
}

customElements.define('cart-shipping-estimate', CartShippingEstimate);
